import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { 
  Shield, Lock, CheckCircle, DollarSign, Package, Clock, 
  Users, Award, AlertTriangle, CreditCard, Truck, Eye,
  BadgeCheck, ShieldCheck, Banknote, UserCheck, Scale, Heart
} from 'lucide-react';

// Trust Badge Component
const TrustBadge = ({ icon: Icon, title, description, color = "cyan" }) => {
  const colorClasses = {
    cyan: "bg-[#00BFFF]/10 border-[#00BFFF]/30 text-[#00BFFF]",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-400",
    purple: "bg-purple-500/10 border-purple-500/30 text-purple-400"
  };
  
  return (
    <div className={`p-6 rounded-xl border ${colorClasses[color]} transition-all hover:scale-[1.02]`}>
      <Icon className="w-10 h-10 mb-4" />
      <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed">{description}</p>
    </div>
  );
};

// Protection Card Component
const ProtectionCard = ({ number, title, subtitle, bullets, icon: Icon, gradient }) => (
  <div className="relative bg-zinc-900 border border-zinc-800 rounded-2xl p-8 overflow-hidden">
    {/* Gradient accent */}
    <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${gradient}`} />
    
    {/* Number badge */}
    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r ${gradient} text-black font-black text-xl mb-6`}>
      {number}
    </div>
    
    <div className="flex items-start gap-4 mb-4">
      <Icon className="w-8 h-8 text-white flex-shrink-0 mt-1" />
      <div>
        <h3 className="text-2xl font-bold text-white">{title}</h3>
        <p className="text-zinc-400 mt-1">{subtitle}</p>
      </div>
    </div>
    
    <ul className="space-y-3 mt-6">
      {bullets.map((bullet, idx) => (
        <li key={idx} className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <span className="text-zinc-300">{bullet}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Micro-copy Snippet Component
const TrustSnippet = ({ icon: Icon, text, context }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">{context}</p>
    <div className="flex items-center gap-2 text-white font-medium">
      <Icon className="w-4 h-4 text-[#00BFFF]" />
      <span>{text}</span>
    </div>
  </div>
);

function TrustSafetyPage() {
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#00BFFF]/5 via-transparent to-transparent" />
        
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00BFFF]/10 border border-[#00BFFF]/30 rounded-full text-[#00BFFF] text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Trust & Safety
            </div>
            
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Every Card. Every Transaction.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00BFFF] to-emerald-400">
                Protected.
              </span>
            </h1>
            
            <p className="text-xl text-zinc-400 leading-relaxed">
              At Slab Society, we believe collectors deserve a marketplace built on verification, not faith. 
              Our platform holds payments in secure escrow until your card arrives exactly as described—because 
              trust isn't a feature, it's our foundation.
            </p>
          </div>
          
          {/* Trust Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <p className="text-3xl font-black text-[#00BFFF]">100%</p>
              <p className="text-zinc-400 text-sm mt-1">Escrow Protected</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <p className="text-3xl font-black text-emerald-400">48hr</p>
              <p className="text-zinc-400 text-sm mt-1">Inspection Window</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <p className="text-3xl font-black text-amber-400">0</p>
              <p className="text-zinc-400 text-sm mt-1">Seller Chargeback Risk</p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <p className="text-3xl font-black text-purple-400">AI</p>
              <p className="text-zinc-400 text-sm mt-1">Card Verification</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Manifesto */}
      <section className="py-16 border-y border-zinc-800">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-[#00BFFF]/10 to-emerald-500/10 border border-[#00BFFF]/20 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-8 h-8 text-[#00BFFF]" />
              <h2 className="text-2xl font-bold text-white">The Trust Manifesto</h2>
            </div>
            <blockquote className="text-xl md:text-2xl text-zinc-300 leading-relaxed italic">
              "We stand between you and risk so you can focus on what matters: building your collection. 
              Every transaction is protected by escrow, every card verified before listing, and every 
              dispute resolved with fairness at its core. This isn't just policy—it's our promise."
            </blockquote>
            <p className="text-[#00BFFF] font-medium mt-6">— The Slab Society Team</p>
          </div>
        </div>
      </section>

      {/* Buyer Protection */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-500/10 rounded-lg">
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
            </div>
            <p className="text-emerald-400 font-medium uppercase tracking-wider text-sm">For Buyers</p>
          </div>
          <h2 className="text-4xl font-black text-white mb-4">Buyer Protection Guarantee</h2>
          <p className="text-zinc-400 text-lg mb-12 max-w-2xl">
            Shop with complete confidence. Your purchase is protected from checkout to delivery.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <ProtectionCard
              number="1"
              title="Secure Escrow Payments"
              subtitle="Your money stays protected until your card is in-hand."
              icon={Lock}
              gradient="from-emerald-400 to-emerald-600"
              bullets={[
                "Funds held via Stripe Connect escrow—sellers don't receive payment until you confirm delivery",
                "Automatic refund if tracking shows non-delivery after 14 days",
                "Dispute resolution team available within 48 hours"
              ]}
            />
            
            <ProtectionCard
              number="2"
              title="Graded Card Verification"
              subtitle="Buy with confidence—what you see is what you get."
              icon={BadgeCheck}
              gradient="from-[#00BFFF] to-blue-600"
              bullets={[
                "AI-powered slab scanner cross-references PSA, BGS, and CGC databases",
                "Cert number verification confirms authenticity before listing goes live",
                "High-resolution image requirements eliminate guesswork"
              ]}
            />
            
            <ProtectionCard
              number="3"
              title="48-Hour Inspection Window"
              subtitle="Examine your card. No pressure. No risk."
              icon={Clock}
              gradient="from-purple-400 to-purple-600"
              bullets={[
                "Full 48 hours after delivery to inspect and confirm condition",
                "One-click return initiation if item doesn't match listing",
                "Prepaid return labels for verified disputes—we cover shipping"
              ]}
            />
          </div>
        </div>
      </section>

      {/* Seller Protection */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Banknote className="w-6 h-6 text-amber-400" />
            </div>
            <p className="text-amber-400 font-medium uppercase tracking-wider text-sm">For Sellers</p>
          </div>
          <h2 className="text-4xl font-black text-white mb-4">Seller Protection Guarantee</h2>
          <p className="text-zinc-400 text-lg mb-12 max-w-2xl">
            List with certainty. Your payouts are guaranteed and your business is protected.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <ProtectionCard
              number="1"
              title="Guaranteed Payouts"
              subtitle="Sell with certainty—your money is never in limbo."
              icon={DollarSign}
              gradient="from-amber-400 to-orange-500"
              bullets={[
                "Funds auto-release 48 hours after confirmed delivery",
                "Direct deposit to your bank within 2-3 business days",
                "No payment reversals once escrow releases"
              ]}
            />
            
            <ProtectionCard
              number="2"
              title="Fraudulent Chargeback Shield"
              subtitle="We fight fraud so you don't have to."
              icon={Shield}
              gradient="from-red-400 to-red-600"
              bullets={[
                "Slab Society absorbs chargeback liability on verified transactions",
                "Tracking confirmation + delivery signature = ironclad proof",
                "Our compliance team handles disputes directly with payment processors"
              ]}
            />
            
            <ProtectionCard
              number="3"
              title="Verified Buyer Requirements"
              subtitle="Only serious collectors can bid on your cards."
              icon={UserCheck}
              gradient="from-cyan-400 to-cyan-600"
              bullets={[
                "Buyers must have validated shipping address via Shippo verification",
                "Payment method on file required before bidding in live auctions",
                "Buyer rating system flags problematic accounts before they reach you"
              ]}
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4">How Protection Works</h2>
            <p className="text-zinc-400 text-lg">Every transaction follows our secure escrow flow</p>
          </div>
          
          <div className="relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-[#00BFFF] via-emerald-400 to-amber-400 -translate-y-1/2" />
            
            <div className="grid md:grid-cols-5 gap-8">
              {[
                { icon: CreditCard, title: "Buyer Pays", desc: "Payment held in secure escrow", color: "text-[#00BFFF]" },
                { icon: Package, title: "Seller Ships", desc: "Tracking uploaded to platform", color: "text-blue-400" },
                { icon: Truck, title: "Item Delivered", desc: "Confirmed via carrier tracking", color: "text-emerald-400" },
                { icon: Eye, title: "48hr Inspection", desc: "Buyer verifies condition", color: "text-purple-400" },
                { icon: Banknote, title: "Funds Released", desc: "Seller receives payout", color: "text-amber-400" }
              ].map((step, idx) => (
                <div key={idx} className="relative text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-900 border-2 border-zinc-700 flex items-center justify-center relative z-10`}>
                    <step.icon className={`w-7 h-7 ${step.color}`} />
                  </div>
                  <h3 className="text-white font-bold mb-1">{step.title}</h3>
                  <p className="text-zinc-500 text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Snippets / Micro-copy */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-white mb-4">Trust at Every Step</h2>
            <p className="text-zinc-400">You'll see these assurances throughout your experience</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <TrustSnippet 
              icon={Lock} 
              text="Escrow-protected until delivery confirmed" 
              context="Under 'Buy Now' Button"
            />
            <TrustSnippet 
              icon={Banknote} 
              text="Guaranteed payout. Zero chargeback risk." 
              context="Under 'List an Item' Button"
            />
            <TrustSnippet 
              icon={Shield} 
              text="Funds held until you receive your card" 
              context="Under 'Place Bid' Button"
            />
            <TrustSnippet 
              icon={CheckCircle} 
              text="Your payment is held securely until delivery" 
              context="On Checkout Page"
            />
            <TrustSnippet 
              icon={Award} 
              text="Payout protected by Slab Society Guarantee" 
              context="On Seller Dashboard"
            />
            <TrustSnippet 
              icon={BadgeCheck} 
              text="Free entry. Verified winners only." 
              context="Under 'Join Giveaway' Button"
            />
          </div>
        </div>
      </section>

      {/* Trust Badges Reference */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-white mb-4">Our Trust Commitments</h2>
            <p className="text-zinc-400">The pillars that protect every transaction</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <TrustBadge 
              icon={Lock}
              title="Stripe-Secured"
              description="All payments processed through Stripe's bank-level security infrastructure."
              color="cyan"
            />
            <TrustBadge 
              icon={BadgeCheck}
              title="Cert Verified"
              description="Every graded card's certification number verified against official databases."
              color="emerald"
            />
            <TrustBadge 
              icon={Scale}
              title="Fair Disputes"
              description="Neutral dispute resolution with evidence-based decisions within 48 hours."
              color="amber"
            />
            <TrustBadge 
              icon={Users}
              title="Community Rated"
              description="Buyer and seller ratings build accountability and reward great members."
              color="purple"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Shield className="w-16 h-16 text-[#00BFFF] mx-auto mb-6" />
          <h2 className="text-4xl font-black text-white mb-4">
            Ready to Trade with Confidence?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">
            Join thousands of collectors who buy and sell with complete peace of mind.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/marketplace"
              className="px-8 py-4 bg-[#00BFFF] hover:bg-[#00BFFF]/80 text-black font-bold rounded-xl transition-all"
            >
              Browse Marketplace
            </Link>
            <Link 
              to="/sell"
              className="px-8 py-4 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl border border-zinc-700 transition-all"
            >
              Start Selling
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-12 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">
            Questions about our protection policies?{' '}
            <Link to="/qa" className="text-[#00BFFF] hover:underline">Visit our FAQ</Link>
            {' '}or{' '}
            <Link to="/messages" className="text-[#00BFFF] hover:underline">contact support</Link>.
          </p>
          <p className="text-zinc-600 text-xs mt-4">
            Last updated: March 2026 | Slab Society Trust & Safety Team
          </p>
        </div>
      </section>
    </div>
  );
}

export default TrustSafetyPage;
